/**
 * Range logic for PMEI parameter tables. Data parameters may define optional
 * numeric or datetime min/max bounds; rendered values that fall outside those
 * bounds are emphasized (bolded) in the side panel.
 *
 * Reuses extractComparableMinMaxValue so float/datetime comparison stays
 * consistent with the config-time setpoint range validation.
 */
import type {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import type {
  DatetimeSchemaFieldTypedValue,
  FloatSchemaFieldTypedValue,
} from 'notebook/tables/typed-values/TypedValueTypes';
import {extractComparableMinMaxValue} from 'procedures/utils/procedureParameterMinMax';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

export type MinMaxTypedValue = FloatSchemaFieldTypedValue | DatetimeSchemaFieldTypedValue | null | undefined;

/** How a single value compares against a parameter's [minimum, maximum] bounds. */
export interface RangeComparison {
  isBelowMinimum: boolean;
  isAboveMaximum: boolean;
}

const IN_RANGE: RangeComparison = {isBelowMinimum: false, isAboveMaximum: false};

type ComparableTypedValue = FloatSchemaFieldTypedValue | DatetimeSchemaFieldTypedValue;

export function isComparableTypedValue(value: TypedValue): value is ComparableTypedValue {
  return value.type === FieldType.FLOAT || value.type === FieldType.DATETIME;
}

/**
 * Compares a single parameter value against the [minimum, maximum] bounds,
 * reporting each direction separately so callers can distinguish below-min from
 * above-max (e.g. for an explanatory tooltip). Bounds are inclusive, and a
 * null/undefined bound means unbounded on that side. An empty value or bound
 * cannot be compared and is ignored, so such a value is never flagged.
 */
export function compareValueToRange(
  value: ComparableTypedValue,
  minimum: MinMaxTypedValue,
  maximum: MinMaxTypedValue
): RangeComparison {
  const comparable = extractComparableMinMaxValue(value);
  if (comparable == null) {
    return IN_RANGE;
  }
  const min = extractComparableMinMaxValue(minimum ?? null);
  const max = extractComparableMinMaxValue(maximum ?? null);
  return {
    isBelowMinimum: min != null && comparable < min,
    isAboveMaximum: max != null && comparable > max,
  };
}

/** Whether the comparison indicates the value is outside the range in either direction. */
export function isOutOfRange(comparison: RangeComparison): boolean {
  return comparison.isBelowMinimum || comparison.isAboveMaximum;
}
